// SPDX-License-Identifier: Elastic-2.0
/**
 * i18n bootstrap.
 *
 * Per technical spec § 12 (Risk register: "i18n bundle bloat — 6 locales"):
 *   - English is the only locale bundled eagerly (static JSON import).
 *   - es / pt / fr / de / it are dynamic-imported on demand via
 *     `loadLocale()` (one async chunk per locale). The orchestrator's
 *     Zustand store (`useLocale`) calls `loadLocale()` before flipping
 *     i18next's active language.
 *
 * Per functional spec § 9:
 *   - Default UI locale is EN (also enforced by `english_standard.md`
 *     project memory).
 *   - Persistence via `localStorage.locale`; first-visit detection
 *     respects `navigator.language` but only when the value maps to one
 *     of the 6 supported locales — otherwise we fall back to EN.
 *   - Missing keys log `console.warn` in dev only; never throw.
 *
 * Compatibility note: this module targets i18next v26 / react-i18next v17.
 * The init-options surface used here (lng, fallbackLng, resources,
 * detection, interpolation, missingKeyHandler, saveMissing) is stable
 * back to v23.
 */
import i18next, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";

/** Supported UI locales at MVP (functional spec § 9). */
export const SUPPORTED_LOCALES = ["en", "es", "pt", "fr", "de", "it"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Default locale (functional spec § 9; project memory `english_standard.md`). */
export const DEFAULT_LOCALE: SupportedLocale = "en";

/** localStorage key for persisted locale choice. */
export const LOCALE_STORAGE_KEY = "locale";

/**
 * Type guard: is the given string one of the supported locales?
 * Used by the detector + the store before persisting / switching.
 */
export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Track which non-EN locales have already been dynamic-imported so we
 * don't pay the JSON-parse cost twice on rapid switches.
 */
const loadedLocales = new Set<SupportedLocale>(["en"]);

/**
 * Dynamic-import a locale JSON and register it with i18next.
 *
 * - EN is bundled eagerly; this function is a no-op for it.
 * - Each other locale becomes its own async chunk thanks to the
 *   `/* @vite-ignore *\/`-free dynamic-import expression — Vite/Rollup
 *   statically analyses the template literal and code-splits per locale.
 *
 * Idempotent: safe to call multiple times for the same locale.
 */
export async function loadLocale(locale: SupportedLocale): Promise<void> {
  if (loadedLocales.has(locale)) {
    return;
  }
  await i18nextReady;
  let resources: Record<string, unknown>;
  switch (locale) {
    case "es":
      resources = (await import("./locales/es.json")).default;
      break;
    case "pt":
      resources = (await import("./locales/pt.json")).default;
      break;
    case "fr":
      resources = (await import("./locales/fr.json")).default;
      break;
    case "de":
      resources = (await import("./locales/de.json")).default;
      break;
    case "it":
      resources = (await import("./locales/it.json")).default;
      break;
    case "en":
      // Already bundled. Should be unreachable thanks to the guard above.
      return;
  }
  i18next.addResourceBundle(locale, "translation", resources, true, true);
  loadedLocales.add(locale);
}

/**
 * Initialize i18next.
 *
 * - Synchronous bundle: EN only (other locales lazy via `loadLocale`).
 * - `lng` is left undefined so the detector runs; we still pass an
 *   explicit `fallbackLng: "en"` so any unmapped value resolves to EN.
 * - `saveMissing: true` is required to make `missingKeyHandler` fire
 *   (per i18next docs); in production we still log nothing.
 */
const isDev = (import.meta as ImportMeta).env?.DEV === true;

const eagerResources: Resource = {
  en: { translation: en },
};

export const i18nextReady = i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: eagerResources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key) => {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(
          `[i18n] missing key "${key}" in ns "${ns}" for lng "${lngs.join(",")}" — falling back to EN.`,
        );
      }
    },
  });

export { i18next };
export default i18next;
